/**
 * App root — React Router + MUI ThemeProvider + React Query.
 */

import { lazyPage } from "@/utils/lazyPage";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider, CssBaseline } from "@mui/material";
import { QueryClientProvider } from "@tanstack/react-query";
import { lightTheme, darkTheme } from "@/styles/theme";
import { useUiStore } from "@/stores/uiStore";
import { useAuthStore } from "@/stores/authStore";
import { useResponsiveTableLabels } from "@/hooks/useResponsiveTableLabels";
import "@/styles/global.css";
import { queryClient } from "@/services/queryClient";

import PrivateRoute from "@/components/auth/PrivateRoute";
import ErrorBoundary from "@/components/common/ErrorBoundary";
import ToastContainer from "@/components/common/ToastContainer";
import PageSuspense from "@/components/common/PageSuspense";
import TelemetryTracker from "@/components/telemetry/TelemetryTracker";
import LoginPage from "@/pages/LoginPage";
import { FEATURE_FLAGS } from "@/config/featureFlags";

const MainLayout = lazyPage(() => import("@/components/layout/MainLayout"));
const DashboardPage = lazyPage(() => import("@/pages/DashboardPage"));
const UsersPage = lazyPage(() => import("@/pages/UsersPage"));
const WorkersPage = lazyPage(() => import("@/pages/WorkersPage"));
const CustomersPage = lazyPage(() => import("@/pages/CustomersPage"));
const SchedulePage = lazyPage(() => import("@/pages/SchedulePage"));
const RouteMapPage = lazyPage(() => import("@/pages/RouteMapPage"));
const WorkLogsPage = lazyPage(() => import("@/pages/WorkLogsPage"));
const PayrollPage = lazyPage(() => import("@/pages/PayrollPage"));
const TasksPage = lazyPage(() => import("@/pages/TasksPage"));
const MaterialsPage = lazyPage(() => import("@/pages/MaterialsPage"));
const BugReportsPage = lazyPage(() => import("@/pages/BugReportsPage"));
const DevTasksPage = lazyPage(() => import("@/pages/DevTasksPage"));
const ZakazkyListPage = lazyPage(() => import("@/pages/ZakazkyListPage"));
const PuProjectDetailPage = lazyPage(() => import("@/pages/PuProjectDetailPage"));
const ReportsPage = lazyPage(() => import("@/pages/ReportsPage"));
const ProfitabilityPage = lazyPage(() => import("@/pages/ProfitabilityPage"));
const UserGroupsPage = lazyPage(() => import("@/pages/UserGroupsPage"));
const ProfilePage = lazyPage(() => import("@/pages/ProfilePage"));
const MyAbsencesPage = lazyPage(() => import("@/pages/MyAbsencesPage"));
const StatisticsPage = lazyPage(() => import("@/pages/StatisticsPage"));
const AbsenceCalendarPage = lazyPage(() => import("@/pages/AbsenceCalendarPage"));
const AttendancePage = lazyPage(() => import("@/pages/AttendancePage"));
const WebhooksPage = lazyPage(() => import("@/pages/WebhooksPage"));
const ApiKeysPage = lazyPage(() => import("@/pages/ApiKeysPage"));
const ErrorLogsPage = lazyPage(() => import("@/pages/ErrorLogsPage"));
const TelemetryPage = lazyPage(() => import("@/pages/TelemetryPage"));
const ServerStatusPage = lazyPage(() => import("@/pages/ServerStatusPage"));
const CrmMirrorPage = lazyPage(() => import("@/pages/CrmMirrorPage"));
const ChatPage = lazyPage(() => import("@/pages/ChatPage"));
const ManualPage = lazyPage(() => import("@/pages/ManualPage"));
const KsenaAiPage = lazyPage(() => import("@/pages/KsenaAiPage"));
const NotFoundPage = lazyPage(() => import("@/pages/NotFoundPage"));
const ForcePasswordChangePage = lazyPage(() => import("@/pages/ForcePasswordChangePage"));
const ForgotPasswordPage = lazyPage(() => import("@/pages/ForgotPasswordPage"));
const ResetPasswordPage = lazyPage(() => import("@/pages/ResetPasswordPage"));

function FeatureGuard({ flag, children }: { flag: keyof typeof FEATURE_FLAGS; children: React.ReactNode }) {
  if (!FEATURE_FLAGS[flag]) return <Navigate to="/404" replace />;
  return <>{children}</>;
}

export default function App() {
  const sessionIdentity = useAuthStore((state) =>
    `${state.user?.id ?? ""}:${state.user?.role ?? ""}:${state.user?.workerId ?? ""}`,
  );
  const themeMode = useUiStore((state) => state.themeMode);
  const theme = themeMode === 'dark' ? darkTheme : lightTheme;
  useResponsiveTableLabels();

  // Set data attribute for CSS variable dark mode support
  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute('data-mui-color-scheme', themeMode);
  }

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <ErrorBoundary>
        <BrowserRouter>
          <TelemetryTracker />
          <PageSuspense>
          <Routes>
            {/* Public */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />

            {/* Protected — any authenticated user */}
            <Route element={<PrivateRoute />}>
              {/* Force password change — must be completed before accessing app */}
              <Route path="/change-password" element={<ForcePasswordChangePage />} />

              <Route element={<MainLayout key={sessionIdentity} />}>
                <Route path="/" element={<DashboardPage />} />
                <Route path="/customers" element={<FeatureGuard flag="customers"><CustomersPage /></FeatureGuard>} />
                <Route path="/schedule" element={<SchedulePage />} />
                <Route path="/route-map" element={<FeatureGuard flag="routeMap"><RouteMapPage /></FeatureGuard>} />
                <Route path="/work-logs" element={<FeatureGuard flag="workLogs"><WorkLogsPage /></FeatureGuard>} />
                <Route path="/tasks" element={<FeatureGuard flag="tasks"><TasksPage /></FeatureGuard>} />
                <Route path="/zakazky" element={<ZakazkyListPage />} />
                <Route path="/zakazky/pu/:projectId" element={<PuProjectDetailPage />} />
                <Route path="/chat" element={<FeatureGuard flag="chat"><ChatPage /></FeatureGuard>} />
                <Route path="/manual" element={<ManualPage />} />
                <Route path="/profile" element={<ProfilePage />} />
                <Route path="/my-absences" element={<MyAbsencesPage />} />
                <Route path="/statistics" element={<StatisticsPage />} />

                {/* Role-restricted */}
                <Route element={<PrivateRoute roles={["admin", "manager", "hr"]} />}>
                  <Route path="/workers" element={<WorkersPage />} />
                  <Route path="/user-groups" element={<UserGroupsPage />} />
                  <Route path="/absence-calendar" element={<AbsenceCalendarPage />} />
                </Route>
                <Route element={<PrivateRoute roles={["admin", "manager"]} />}>
                  <Route path="/users" element={<UsersPage />} />
                  <Route path="/materials" element={<MaterialsPage />} />
                  <Route path="/bug-reports" element={<BugReportsPage />} />
                  <Route path="/ksena-ai" element={<FeatureGuard flag="ksenaAi"><KsenaAiPage /></FeatureGuard>} />
                </Route>
                <Route element={<PrivateRoute roles={["admin"]} />}>
                  <Route path="/dev-tasks" element={<DevTasksPage />} />
                  <Route path="/webhooks" element={<WebhooksPage />} />
                  <Route path="/api-keys" element={<ApiKeysPage />} />
                  <Route path="/error-logs" element={<ErrorLogsPage />} />
                  <Route path="/analytics" element={<TelemetryPage />} />
                  <Route path="/server" element={<ServerStatusPage />} />
                </Route>
                <Route element={<PrivateRoute roles={["admin", "manager", "hr", "accountant"]} />}>
                  <Route path="/attendance" element={<FeatureGuard flag="attendance"><AttendancePage /></FeatureGuard>} />
                </Route>
                <Route element={<PrivateRoute roles={["admin", "manager", "accountant"]} />}>
                  <Route path="/payroll" element={<FeatureGuard flag="payroll"><PayrollPage /></FeatureGuard>} />
                  <Route path="/reports" element={<FeatureGuard flag="statistics"><ReportsPage /></FeatureGuard>} />
                  <Route path="/profitability" element={<FeatureGuard flag="profitability"><ProfitabilityPage /></FeatureGuard>} />
                  <Route path="/crm-mirror" element={<CrmMirrorPage />} />
                </Route>

                {/* 404 */}
                <Route path="*" element={<NotFoundPage />} />
              </Route>
            </Route>
          </Routes>
          </PageSuspense>
        </BrowserRouter>
        <ToastContainer />
        </ErrorBoundary>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
